import { resetPassword } from '@/lib/auth-client';
import { routes } from '@/lib/routes';
import { CheckCircle, Loader2, Lock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    if (password.length < 8) {
      setError(t('auth.weakPassword'));
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t('common.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="text-center space-y-4">
        <h2 className="text-2xl font-medium text-ink">{t('auth.invalidToken')}</h2>
        <p className="text-sepia">{t('auth.invalidResetToken')}</p>
        <Link
          to={routes.forgotPassword}
          className="inline-block mt-4 text-blue hover:underline font-medium"
        >
          {t('auth.requestNewLink')}
        </Link>
      </div>
    );
  }

  if (success) {
    return (
      <div className="text-center space-y-4">
        <div className="mx-auto w-12 h-12 bg-green/10 rounded-full flex items-center justify-center">
          <CheckCircle className="w-6 h-6 text-green" />
        </div>
        <h2 className="text-2xl font-medium text-ink">{t('auth.passwordResetSuccess')}</h2>
        <p className="text-sepia">{t('auth.passwordResetSuccessDesc')}</p>
        <Link
          to={routes.login}
          className="inline-block mt-4 py-2.5 px-6 bg-ink text-cream rounded-md font-medium hover:bg-ink/90 transition-all"
        >
          {t('auth.signIn')}
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-medium text-ink">{t('auth.resetPassword')}</h2>
        <p className="text-sepia mt-2">{t('auth.resetPasswordDesc')}</p>
      </div>

      {error && (
        <div className="bg-red/10 border border-red/20 text-red rounded-md p-3 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-sepia mb-1.5">
            {t('auth.newPassword')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-warm" />
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-warm rounded-md focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
              placeholder={t('auth.newPassword')}
            />
          </div>
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-sepia mb-1.5">
            {t('auth.confirmPassword')}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-warm" />
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-warm rounded-md focus:outline-none focus:ring-2 focus:ring-blue/30 focus:border-blue transition-all"
              placeholder={t('auth.confirmPassword')}
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 bg-ink text-cream rounded-md font-medium hover:bg-ink/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('common.loading')}
          </>
        ) : (
          t('auth.resetPassword')
        )}
      </button>
    </form>
  );
}
